import { Link } from "react-router-dom";
import { Package, TrendingUp, Star, Home as HomeIcon, Zap, MessageCircle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useApp } from "@/hooks/useApp";
import { pickLang } from "@/lib/i18n";
import OficialBadge from "@/components/OficialBadge";

export interface ProductCardData {
  id: string;
  name: string;
  name_en?: string | null;
  short_description: string;
  short_description_en?: string | null;
  price: number;
  category: string;
  sales_count: number;
  stock: number;
  image_url?: string | null;
  custom_category_id?: string | null;
  custom_category_name_pt?: string | null;
  custom_category_name_en?: string | null;
  featured?: boolean;
  home_page?: boolean;
  rank_score?: number;
  supplier_is_official?: boolean;
  delivery_mode?: "automatic" | "on_demand";
}


export default function ProductCard({ p, variant = "default" }: { p: ProductCardData; variant?: "default" | "home" }) {
  const { t, lang } = useApp();
  const out = p.delivery_mode !== "on_demand" && p.stock <= 0;
  const name = pickLang(p.name, p.name_en, lang) || p.name;
  const desc = pickLang(p.short_description, p.short_description_en, lang) || p.short_description;
  const catLabel = pickLang(p.custom_category_name_pt ?? "", p.custom_category_name_en ?? "", lang) || p.category;
  const isHomeView = variant === "home";
  // On home view: never show the "Destaque" frame; instead show the "Página principal" frame for pinned products.
  const showFeaturedFrame = Boolean(p.featured) && !isHomeView;
  const showHomeFrame = isHomeView && Boolean(p.home_page);
  const frameClass = showHomeFrame
    ? "p-[2px] bg-gradient-to-br from-accent via-primary to-accent shadow-[0_0_24px_-6px_hsl(var(--accent)/0.6)]"
    : showFeaturedFrame
      ? "p-[2px] bg-gradient-to-br from-primary via-accent to-primary shadow-[0_0_24px_-6px_hsl(var(--primary)/0.6)]"
      : "";
  return (
    <div className={`relative rounded-xl ${frameClass}`}>
      {showHomeFrame && (
        <span className="absolute -top-2 left-3 z-10 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide bg-accent text-accent-foreground shadow">
          <HomeIcon className="w-3 h-3" />
          {lang === "en" ? "Home pick" : "Página principal"}
        </span>
      )}
      {showFeaturedFrame && (
        <span className="absolute -top-2 left-3 z-10 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide bg-primary text-primary-foreground shadow">
          <Star className="w-3 h-3 fill-current" />
          {lang === "en" ? "Featured" : "Destaque"}
        </span>
      )}
      <div className={`glow-card rounded-[inherit] overflow-hidden flex flex-col ${showHomeFrame || showFeaturedFrame ? "bg-card" : "border border-border"}`}>


      {p.image_url ? (
        <Link to={`/produto/${p.id}`} aria-label={name} className="block aspect-[16/10] overflow-hidden bg-secondary/40">
          <img src={p.image_url} alt={name} loading="lazy" className="w-full h-full object-cover transition-transform duration-300 hover:scale-105" />
        </Link>
      ) : (
        <Link to={`/produto/${p.id}`} aria-label={name} className="block aspect-[16/10] bg-gradient-to-br from-primary/10 via-accent/10 to-secondary/40 flex items-center justify-center">
          <Package className="w-10 h-10 text-muted-foreground/40" aria-hidden="true" />
        </Link>
      )}
      <div className="p-5 flex flex-col gap-4 flex-1">
        <div className="flex items-start justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-1.5 flex-wrap">
            <Badge variant="secondary" className="capitalize">{catLabel}</Badge>
            {p.supplier_is_official && <OficialBadge />}
            {p.delivery_mode === "on_demand" ? (
              <Badge variant="outline" className="text-xs"><MessageCircle className="w-3 h-3 mr-1" />{lang === "en" ? "On demand" : "Sob demanda"}</Badge>
            ) : (
              <Badge variant="outline" className="text-xs border-primary/40 text-primary"><Zap className="w-3 h-3 mr-1" />{lang === "en" ? "Instant" : "Imediata"}</Badge>
            )}
          </div>
          {p.delivery_mode === "on_demand" ? null : out ? (
            <Badge variant="destructive" className="bg-[hsl(0_75%_38%)] text-white border-transparent">{t("out")}</Badge>
          ) : (
            <Badge className="bg-success/15 text-success border-success/30 hover:bg-success/20"><Package className="w-3 h-3 mr-1" />{p.stock} {t("in_stock")}</Badge>
          )}
        </div>

        <div>
          <h3 className="font-semibold text-lg leading-tight mb-1">{name}</h3>
          <p className="text-sm text-muted-foreground line-clamp-2">{desc}</p>
        </div>
        <div className="flex items-center gap-1 text-xs text-muted-foreground">
          <TrendingUp className="w-3 h-3" /> {p.sales_count} {t("sales")}
        </div>
        <div className="mt-auto flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
          <div>
            <div className="text-xs text-muted-foreground">{t("from")}</div>
            <div className="text-2xl font-bold gradient-text">R$ {Number(p.price).toFixed(2)}</div>
          </div>
          {out ? (
            // Out-of-stock: render a real disabled <button>. Avoids two product cards
            // sharing the identical anchor text "Esgotado" linking to different URLs
            // (Seobility "identical anchor text for different pages").
            <Button type="button" disabled aria-label={`${t("out")}: ${name}`} className="w-full sm:w-auto">{t("out")}</Button>
          ) : (
            // Visible label includes the product name so anchor text is unique
            // per product (Seobility flagged "Comprar" repeated across products).
            <Button asChild className="w-full sm:w-auto">
              <Link to={`/produto/${p.id}`} aria-label={`${t("buy")} ${name}`}>
                <span className="truncate max-w-full sm:max-w-[10rem]">{t("buy")} · {name.split(" ").slice(0, 3).join(" ")}</span>
              </Link>
            </Button>
          )}
        </div>
      </div>
      </div>
    </div>
  );
}

